import { PreRenderService } from './../../services/pre-render.service';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ElementRef, ViewChild, AfterViewInit} from '@angular/core';
import {ListingsService} from '../../services/listings.service';
import {Listing} from '../../classes/listing';
import {HasSubscriptions} from '../has-subscriptions';
import {CommunicationService} from '../../services/communication.service';
import {TranslateService} from '@ngx-translate/core';
import {SCREEN_SIZE, SortBy} from '../../classes/enums';
import {SelectOption} from '../forms/select/select.component';
import {PreferencesService} from '../../services/preferences.service';
import {FormBuilder, FormGroup} from '@angular/forms';
import {Office} from '../../classes/office';
import {Pagination} from '../../classes/pagination';
import {Agent} from '../../classes/agent';
import { Helper } from '../../classes/helper';
import { merge } from 'rxjs';
import {debounceTime, delay} from 'rxjs/operators';
import { PositionInfo } from '../../classes/user-preferences';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { INFINITE_SCROLL } from '../../classes/analytics-tags';


/**
 * We try to design the gallery so that its in as many ways as possible, just a component that worries about its own view, and doesn't worry about
 * data retrieval and other functions that don't exist solely for this component
 *
 * Items are passed in via the parent, and the logic that decided which elements to show are also decided by the parent
 */
export class BaseGalleryViewComponent extends HasSubscriptions implements OnInit, OnChanges {
  sortForm: FormGroup;
  totalResultsFound: number = 0;
  // TODO: fix this - See if there's a better way to do sorting options that changes depending on the type of the items
  sortOptions: SelectOption[] = [
    {
      key: 'gallery.sortby.newest',
      value: SortBy.NEWEST
    } as SelectOption,
    {
      key: 'gallery.sortby.oldest',
      value: SortBy.OLDEST
    } as SelectOption,
  ];
  // TODO: whats the difference here
  hasMoreResultsToLoad: boolean = true;

  @Input() readonly shouldHideSorting: boolean = false;
  @Input() readonly isFetchingItems: boolean = true;
  @Input() pagination: Pagination = new Pagination();
  @Input() readonly city: string;
  @Input() readonly province: string;
  @Input() items: (Listing | Agent | Office)[] = []; // TODO: Obsolete
  @Output() itemClicked: EventEmitter<Listing | Office | Agent> = new EventEmitter();
  @Output() infiniteScrolled: EventEmitter<void> = new EventEmitter();
  @Output() changePage: EventEmitter<number> = new EventEmitter();


  protected _screenSize: SCREEN_SIZE;
  protected _positionInfo: PositionInfo;
  // Enums
  Listing = Listing;
  relatedOffice: Office;

  constructor(
    protected _preferencesService: PreferencesService,
    protected _communicationService: CommunicationService,
    protected _translate: TranslateService,
    protected _fb: FormBuilder,
    protected _listingsService: ListingsService,
    protected _prerenderService: PreRenderService,
    protected _analyticsService: AnalyticsService
  ) {
    super();
    this.sortForm = this._fb.group({
      sort: SortBy.NEWEST
    });

    this.sortForm.controls['sort'].setValue(this._preferencesService.getGallerySorting());
  }

  ngOnInit() {
    this.addSub(this._communicationService.screenSize.subscribe((s) => this._screenSize = s));
  }

  ngOnChanges(changes: SimpleChanges) {}
  sortItems() {}
  onGalleryScrolled() {
    // Only emit the infinite scroll for mobile since we use pagination for all greater sizes
    if (this._screenSize === SCREEN_SIZE.MOBILE_ONLY) this.infiniteScrolled.emit()
  }
}

@Component({
  selector: 'app-listing-gallery-view',
  templateUrl: './listing-gallery-view/listing-gallery-view.component.html',
  styleUrls: ['gallery-view.component.scss', './listing-gallery-view/listing-gallery-view.component.scss']
})
export class ListingGalleryViewComponent extends BaseGalleryViewComponent implements OnInit, OnChanges, AfterViewInit {

  @Input() toggleListingGallery: boolean = false;
  @Input() isFavouritesPage: boolean = false; // Whether we're looking at the favourites gallery page
  @Input() selectedClusterListings: Listing[] = []; // The first listing to show should be the listings that are part of the cluster they selected if any, can also be favourites
  @Input() confirmUnfavourite: boolean = false; // Whether we need to show a confirm message when they're unfavouriting an item

  // TODO: migrate this to follow more the base
  isDoneFetchingAllItems: boolean = true;

  sortOptions: SelectOption[] = [
    {
      key: 'gallery.sortby.newest',
      value: SortBy.NEWEST
    } as SelectOption,
    {
      key: 'gallery.sortby.oldest',
      value: SortBy.OLDEST
    } as SelectOption,
    {
      key: 'gallery.sortby.priceLowToHigh',
      value: SortBy.PRICE_LOW_TO_HIGH
    } as SelectOption,
    {
      key: 'gallery.sortby.priceHighToLow',
      value: SortBy.PRICE_HIGH_TO_LOW
    } as SelectOption
  ];

  private selectedClusterListingPairs: {listingId: string, detailUrl: string}[] = []; // A mapping of the selected cluster listings to their IDs

  ngOnInit() {
    super.ngOnInit();
    this.addSub(this.sortForm.valueChanges.pipe(debounceTime(200)).subscribe((value) => {
      this._preferencesService.setGallerySorting(value.sort);
      if (this.selectedClusterListings && this.selectedClusterListings.length) this.sortSelectedClusterListings();
    }));

    this.addSub(this._communicationService.screenSize.subscribe((s) => this._screenSize = s));
    this.addSub(this._preferencesService.positionInfoChanged.subscribe(positionInfo => this._positionInfo = positionInfo));
  }

  ngOnChanges(changes: SimpleChanges) {

    // In mobile, this component is always displayed so when first initialized there won't be a location to use, we use this below to ensure that
    // some listings will display when we go to this gallery mode
    if (changes.toggleListingGallery && this.toggleListingGallery) {
      this.getMoreListings();
    }

    // If the selected listings change, we manually sort this ourselves, since we don't make a call to the server to get the selected listings sorted
    if (changes.selectedClusterListings && changes.selectedClusterListings.currentValue.length) {
      this.selectedClusterListingPairs = (this.selectedClusterListings || []).map(l => ({listingId: l.listingId, detailUrl: l.detailUrl}));
      // TODO: Sort this.primaryListings in memory using sort criteria
      this.sortSelectedClusterListings();
    }
  }

  ngAfterViewInit() {
    // Make sure to reset the page number to get the listings to fetch properly and listings
    // TODO: should this be generalized and go into the base
    this.addSub(
      merge(
        this._communicationService.searchParametersChanged,
        this.sortForm.controls['sort'].valueChanges,
        this._communicationService.listings.currentShownListingType)
        .pipe(delay(0),debounceTime(400))
        .subscribe(() => {
          // TODO: go up a level
          this.pagination.pageNumber = 0;
          this.hasMoreResultsToLoad = true;
          this.items = [];
          this.getMoreListings();
        })
    );
  }

  onInfiniteScroll() {
    this._analyticsService.eventTrack(INFINITE_SCROLL, null);
  }

  /**
   * Whether or not we get more listings. If it's favourites, we don't paginate that so there's no need to get more listings
   */
  getMoreListings() {
    if (this.isFavouritesPage || this._prerenderService.isServerMode) return;
    if (this.hasMoreResultsToLoad) this.getItems();
  }

  /**
   * Sort the selected cluster listings. We need to do this manually since these listings aren't coming sorted from the server via a service call, but instead from the map
   */
  sortSelectedClusterListings() {
    const val = this.sortForm.get('sort').value;
    // TODO: should this go into the Listing class instead
    this.selectedClusterListings.sort((o1: Listing, o2: Listing) => Listing.sortByMixed(o1, o2, val));
  }

  /**
   * Makes calls to retrieve more listings
   */
  // TODO: should this be dealt with by the parent
  async getItems() {
    this.isDoneFetchingAllItems = false;

    // keep requesting pages until either there are no more listings, or we find a page that includes something not already in our primaryListings.
    while (true) {
      let response;
      // We need to consider the case where no position info is provided or the google location lookup services fail (ex: Google Bot) and no position will be available
      // Default Gallery search requires a location so we will need to have an extra fallback in that situation
      if (!this._positionInfo || (this._positionInfo && !this._positionInfo.east && !this._positionInfo.west && !this._positionInfo.north && !this._positionInfo.south)) {
        response = await this._listingsService.searchProvinceCity(this.province, this.city, this.pagination, null, this.sortForm.get('sort').value ).toPromise();
      }
      else {
        response = await this._listingsService.searchListingsGallery(this.pagination, this.sortForm.get('sort').value, true);
      }
      if (response === null || response.results === null || response.results.length === 0) this.hasMoreResultsToLoad = false;
      else {
        if (response.office) {
          this.relatedOffice = response.office;
          this.relatedOffice.telephone = Helper.formatPhoneNumber(this.relatedOffice.telephone);
        }
        this.pagination.setTotalResults(response.totalHits);

        this.totalResultsFound = response.totalHits;
        // For the new page of listings, make sure to only show the listings that aren't already being shown in the selectedCluster listings
        const newPage = response.results.filter(l => this.selectedClusterListingPairs.map((cl) => cl.listingId).indexOf(l.listingId) < 0);
        this.pagination.pageNumber++;
        // If the all the listings from the new page are duplicates, get new listings
        if (!newPage || newPage.length === 0) continue;

        this.items.push(...newPage);
      }
      break;
    }
    this.isDoneFetchingAllItems = true;
    // TODO: dont use instant here
    this._communicationService.notifications.notificationChanged.next(this._translate.instant('notification.n-listings-found', {n: this.totalResultsFound}));
  }


  /**
   * When they click on a listing card, we need to include the next listings that they can page through on the LDP
   * @param listing
   */
  onGalleryCardClicked(listing: Listing) {
    // Combine all the listings together first
    const listings = this.selectedClusterListingPairs.concat(this.items.map((l: Listing) => ({listingId: l.listingId, detailUrl: l.detailUrl})));
    const offset = listings.findIndex((l) => l.listingId === listing.listingId);
    this._preferencesService.addNextListings(listings.map((l) => ({listingId: l.listingId, detailUrl: l.detailUrl})).slice(offset, listings.length));
    this.itemClicked.emit(listing);
  }
}

@Component({
  selector: 'app-gallery-view',
  templateUrl: './gallery-view.component.html',
  styleUrls: ['./gallery-view.component.scss']
})
export class GalleryViewComponent extends BaseGalleryViewComponent implements OnInit, OnChanges {}



